import 'reactflow/dist/style.css';
import 'reactflow/dist/base.css';
import styled from '@emotion/styled';
import {
	attachNewEdgeInteractions,
	createNewNode,
	detachEdgeInteractions,
	extractInteractionId,
	generateEdge,
	generateEdgeId,
	getAllTasks,
	getNextTaskId,
	NodeTypes
} from '@utils/flows';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import ReactFlow, {
	addEdge,
	Background,
	Connection,
	Edge,
	Node,
	ReactFlowInstance,
	ReactFlowProps,
	useEdgesState,
	useNodesState,
	OnSelectionChangeParams
} from 'reactflow';
import {
	ACTION_NODE_WIDTH,
	IEdgeData,
	IFlowScreens,
	IFlowsMetadata,
	INodeData,
	ITaskNodeData,
	NEW_SCREEN_PREFIX,
	HiddenActionInputs,
	Task,
	Tasks,
	TaskType,
	FLOW_ACTIONS_AUTOMATICALLY_OPEN
} from 'types/flows';
import { isEmpty } from 'lodash';
import { useKeyboard } from 'hooks';
import ActionableNode from './ActionableNode';
import CustomEdge from './ButtonEdge';
import StartNode from './StartNode';
import AddTaskButton from './Overlays/AddTaskButton';
import CustomControls from './Overlays/Controls/FlowControls';
import AddTaskPreview from './Overlays/AddTaskPreview';
import EndNode from './EndNode';

const edgeTypes = {
	buttonedge: CustomEdge
};
const nodeTypes = {
	[NodeTypes.actionNode]: ActionableNode,
	[NodeTypes.startNode]: StartNode,
	[NodeTypes.endNode]: EndNode
};

const StyledBackground = styled(Background)`
	circle {
		fill: #a0a0a0;
	}
`;

const StyledReactFlow = styled(ReactFlow)`
	.react-flow__attribution {
		display: none;
	}
	.react-flow__node {
		z-index: -1 !important;
	}
	.react-flow__controls-button svg {
		max-width: unset;
		max-height: unset;
	}
	.react-flow__controls-button:hover {
		background-color: unset;
	}
	.react-flow__controls-button {
		width: 46px;
		height: 40px;
		padding: 0;
		display: flex;
		border-bottom: none;
		background-color: inherit;
		align-items: center;
	}
`;

const FlowView: FC<{
	initialNodes: Node<ITaskNodeData>[];
	initialEdges: Edge<IEdgeData>[];
	onChange?: () => void;
	onEditTask?: (task: Task) => void;
	onDeleteTasks?: (tasks: Task[]) => void;
	onScreenAdd?: (screens: IFlowScreens) => void;
	metadata?: IFlowsMetadata;
	setRef?: (ref: ReactFlowInstance<ITaskNodeData, IEdgeData>) => void;
	options?: Partial<ReactFlowProps>;
}> = ({
	onChange,
	metadata,
	initialNodes,
	initialEdges,
	onEditTask,
	onDeleteTasks,
	onScreenAdd,
	setRef,
	options
}) => {
	const [rfInstance, setRfInstance] = useState<ReactFlowInstance<
		ITaskNodeData,
		IEdgeData
	> | null>(null);
	const [draggingState, setDraggingState] = useState<{
		nodes: Node<ITaskNodeData>[];
		edges: Edge<IEdgeData>[];
		screens?: IFlowScreens;
		initialPosition: { x: number; y: number };
	} | null>(null);
	const [nodes, setNodes, onNodesChange] =
		useNodesState<INodeData>(initialNodes);
	const [edges, setEdges, onEdgesChange] =
		useEdgesState<IEdgeData>(initialEdges);
	const [selectedNodeId, setSelectedNodeId] = useState('');

	useEffect(() => {
		if (rfInstance) setRef?.(rfInstance);
	}, [rfInstance, setRef]);

	const markConnectedNodes = useCallback(
		(node: Node<INodeData>) => {
			const nodesToMark = new Set();
			nodesToMark.add(node.id);
			setEdges((eds) =>
				eds.map((i) => {
					const marked = node.id === i.source || node.id === i.target;
					if (marked) {
						nodesToMark.add(i.source);
						nodesToMark.add(i.target);
					}
					return {
						...i,
						data: i.data ? { ...i.data, marked } : undefined
					};
				})
			);
			setNodes((allNodes) =>
				allNodes.map((n) => ({
					...n,
					data: { ...n.data, marked: nodesToMark.has(n.id) }
				}))
			);
		},
		[setEdges, setNodes]
	);

	const deleteTasks = useCallback(
		(tasks: Task[]) => {
			const ids = tasks.map((n) => n.id);
			setNodes((allNodes) => allNodes.filter((n) => !ids.includes(n.id)));
			setEdges((allEdges) =>
				allEdges.filter(
					(e) => !ids.includes(e.target) && !ids.includes(e.source)
				)
			);

			onDeleteTasks?.(tasks);
		},
		[setNodes, setEdges, onDeleteTasks]
	);

	const onNodesDelete = useCallback(
		(deleteNodes: Node<ITaskNodeData>[]) => {
			deleteTasks(
				deleteNodes
					.filter((node) => node.data.task)
					.map((node) => node.data.task!)
			);
		},
		[deleteTasks]
	);

	const onDeleteTask = useCallback(
		(task: Task) => {
			deleteTasks([task]);
		},
		[deleteTasks]
	);

	const onEdgeDelete = useCallback(
		(edge: Edge | Connection) => () => {
			const interactionId = extractInteractionId(edge);
			const newEdgeId = generateEdgeId(
				edge.source!,
				edge.target!,
				interactionId!
			);
			setEdges((allEdges) => allEdges.filter((e) => e.id !== newEdgeId));
			setNodes((allNodes) =>
				allNodes.map((node) => ({
					...node,
					data: {
						...node.data,
						interactions: detachEdgeInteractions(node, edge)
					} as INodeData
				}))
			);
			const selectedNode = rfInstance?.getNodes().find((n) => n.selected);
			if (selectedNode) {
				markConnectedNodes(selectedNode);
			}
			onChange?.();
		},
		[setEdges, setNodes, onChange, rfInstance, markConnectedNodes]
	);

	const onConnect = useCallback(
		(params: Edge | Connection) => {
			const id = extractInteractionId(params);
			let isMarked = false;
			if (rfInstance) {
				isMarked = rfInstance
					.getNodes()
					.some(
						(node) =>
							(node.id === params.target || node.id === params.source) &&
							node.selected
					);
			}

			const newEdge: Edge<IEdgeData> = {
				...params,
				...generateEdge(
					params.source!,
					{ taskId: params.target!, interactionId: id },
					getAllTasks(rfInstance),
					{ marked: isMarked }
				)
			};
			newEdge.data!.onDelete = onEdgeDelete(params);
			setEdges((eds) => addEdge(newEdge, eds));
			setNodes((allNodes) =>
				allNodes.map((node) => {
					const currentTask = (node.data as ITaskNodeData).task || {
						next: { rules: [] }
					};
					const rules = (currentTask.next?.rules || []).reduce(
						(prev, curr) => ({ ...prev, [curr.interactionId!]: curr }),
						{} as Record<string, any>
					);
					rules[id] = { taskId: params.target!, interactionId: id };
					return {
						...node,
						data: {
							...node.data,
							task:
								node.id === params.source
									? {
											...currentTask,
											next: {
												...currentTask.next,
												rules: Object.values(rules)
											}
									  }
									: currentTask,
							interactions: attachNewEdgeInteractions(node, newEdge),
							marked:
								node.data.marked ||
								((node.id === params.source || node.id === params.target) &&
									isMarked)
						} as INodeData
					};
				})
			);
			onChange?.();
		},
		[rfInstance, onEdgeDelete, setEdges, setNodes, onChange]
	);

	const onNodeClick = useCallback(
		(_: React.MouseEvent, node: Node<INodeData>) => {
			if (selectedNodeId !== node.id) {
				setSelectedNodeId(node.id);
				markConnectedNodes(node);
			}
		},
		[markConnectedNodes, selectedNodeId, setSelectedNodeId]
	);

	const onStopDrag = useCallback(
		() => setDraggingState(null),
		[setDraggingState]
	);
	const dragOptions = useMemo(
		() => ({ disabled: !draggingState }),
		[draggingState]
	);
	useKeyboard('Escape', onStopDrag, null, dragOptions);

	const onStartAddNodes = useCallback(
		(tasks: Task[], e: React.MouseEvent, screens?: IFlowScreens) => {
			let allTasks = getAllTasks(rfInstance);
			const idsToNewIdsMap: Record<string, string> = {};
			const screensToAdd: IFlowScreens = {};
			const newNodes: Node<ITaskNodeData>[] = [];
			const newEdges: Edge<IEdgeData>[] = [];
			const newTasks: Tasks = {};
			tasks.forEach((task, i) => {
				const newId = getNextTaskId(allTasks);
				if (
					task.type === TaskType.screen &&
					task.screenId === NEW_SCREEN_PREFIX
				) {
					task.screenId += newId;
				}
				task.view = {
					x: i * 45,
					y: i * 45
				};

				if (task.id) {
					idsToNewIdsMap[task.id] = newId;
					if (screens?.[`${NEW_SCREEN_PREFIX}${task.id}`]) {
						const newScreenId = `${NEW_SCREEN_PREFIX}${newId}`;
						screensToAdd[newScreenId] = {
							...screens[`${NEW_SCREEN_PREFIX}${task.id}`],
							id: newScreenId
						};
						task.screenId = newScreenId;
					}
				}

				newTasks[newId] = task;
				allTasks = { ...allTasks, ...newTasks };
			});

			Object.keys(newTasks).forEach((newId) => {
				const newTask = newTasks[newId];
				newTask.id = newId;
				Object.keys(newTask.arguments || {}).forEach((key) => {
					if (key === HiddenActionInputs.verifyStepId) {
						newTask.arguments![key].value =
							idsToNewIdsMap[newTask.arguments![key].value];
					}
				});

				newTask.next?.rules?.forEach((rule) => {
					if (idsToNewIdsMap[rule.taskId])
						rule.taskId = idsToNewIdsMap[rule.taskId];
					const newEdge = generateEdge(newId, rule, allTasks);
					newEdge.data!.onDelete = onEdgeDelete(newEdge);
					newEdges.push(newEdge);
				});
				newNodes.push(
					createNewNode(
						newId,
						newTask,
						allTasks,
						{ onEditTask, onDeleteTask },
						metadata,
						screensToAdd
					)
				);
			});

			setDraggingState({
				nodes: newNodes,
				edges: newEdges,
				screens: screensToAdd,
				initialPosition: { x: e.clientX, y: e.clientY }
			});
		},
		[
			rfInstance,
			setDraggingState,
			onEditTask,
			onDeleteTask,
			onEdgeDelete,
			metadata
		]
	);

	const onNodeDoubleClick = useCallback(
		(_: React.MouseEvent, node: Node<ITaskNodeData>) => {
			if (node.data.task) {
				onEditTask?.(node.data.task);
			}
		},
		[onEditTask]
	);

	const onAddNodes = useCallback(
		(e: React.MouseEvent) => {
			if (!draggingState) return;
			const position = rfInstance?.project({
				x: e.clientX - 335,
				y: e.clientY - 115
			});
			const {
				nodes: newNodes,
				edges: newEdges,
				screens: screensToAdd
			} = draggingState;
			newNodes.forEach((node, i) => {
				const p = {
					x: (position?.x || 0) + i * (ACTION_NODE_WIDTH + 120),
					y: position?.y || 0
				};
				node.position = p;
				node.data.task!.view = p;
			});
			if (newNodes.length > 0) {
				setNodes((allNodes) => [...allNodes, ...newNodes]);
			}
			if (newEdges.length > 0) {
				setEdges((allEdges) => [...allEdges, ...newEdges]);
			}
			if (!isEmpty(screensToAdd)) {
				onScreenAdd?.(screensToAdd);
			}
			setDraggingState(null);
			onChange?.();

			if (
				newNodes.length === 1 &&
				newNodes[0].data.task &&
				(newNodes[0].data.task.type !== TaskType.automated ||
					FLOW_ACTIONS_AUTOMATICALLY_OPEN.includes(
						newNodes[0].data.task.action || ''
					))
			) {
				setTimeout(() => onEditTask?.(newNodes[0].data.task!), 150);
			}
		},
		[
			rfInstance,
			setNodes,
			setEdges,
			onScreenAdd,
			onEditTask,
			onChange,
			setDraggingState,
			draggingState
		]
	);

	useEffect(() => {
		setEdges((allEdges) =>
			allEdges.map((edge) => {
				edge.data = {
					...edge.data,
					type: edge.data?.type,
					onDelete: onEdgeDelete(edge),
					nextTask: edge.data?.nextTask!
				};
				return edge;
			})
		);
		setNodes((allNodes) =>
			allNodes.map((node) => {
				node.data = {
					...node.data,
					onDeleteTask
				} as ITaskNodeData;
				return node;
			})
		);
	}, [setEdges, onDeleteTask, setNodes, onEdgeDelete, rfInstance]);

	const onSelectionChanged = useCallback(
		(params: OnSelectionChangeParams) => {
			// handle deselect nodes, unmark all. Select is handled in onNodeSelect
			if (!params || params.nodes.length === 0) {
				setSelectedNodeId('');
				setNodes((allNodes) =>
					allNodes.map((n) => ({
						...n,
						data: { ...n.data, marked: false }
					}))
				);
				setEdges((allEdges) =>
					allEdges.map((e) => ({
						...e,
						data: { ...e.data, marked: false } as IEdgeData
					}))
				);
			}
		},
		[setNodes, setEdges, setSelectedNodeId]
	);

	const actionProps: ReactFlowProps = {
		onNodesChange,
		onEdgesChange,
		onConnect,
		onInit: setRfInstance,
		onNodeDragStart: onNodeClick,
		onNodeClick,
		onClick: onAddNodes,
		onSelectionChange: onSelectionChanged,
		onNodeDoubleClick,
		onNodeDrag: onChange,
		onNodesDelete
	};

	return (
		<StyledReactFlow
			nodes={nodes}
			edges={edges}
			minZoom={0.05}
			edgeTypes={edgeTypes}
			nodeTypes={nodeTypes}
			selectNodesOnDrag
			panOnScroll
			elevateEdgesOnSelect
			edgesFocusable={false}
			deleteKeyCode="Delete"
			fitView
			nodesDraggable
			fitViewOptions={{ maxZoom: 1 }}
			attributionPosition="top-right"
			{...actionProps}
			{...options}
		>
			<CustomControls />
			<AddTaskButton onAddTasks={onStartAddNodes} metadata={metadata} />
			{draggingState && <AddTaskPreview state={draggingState} />}
			<StyledBackground gap={16} />
		</StyledReactFlow>
	);
};

FlowView.defaultProps = {
	setRef: undefined,
	onChange: undefined,
	onEditTask: undefined,
	onDeleteTasks: undefined,
	onScreenAdd: undefined,
	metadata: undefined,
	options: undefined
};

export default FlowView;
